import { useEffect, useState, useRef } from "react";
import {
    Box,
    Stack,
    Typography,
    Textarea as JoyTextarea,
    Avatar,
    Skeleton,
} from "@mui/joy";
import { Pencil, Trash2, Check } from "lucide-react";
import moment from "moment";

import { useNotes } from "../../hooks/query";
import { useAuth } from "../../hooks";
import { Button, IconButton, Alert } from "../../shared";
import { INote } from "../../interfaces";

interface NotesProps {
    entityType: string;
    entityId: string;
    height?: string | number;
    density?: "normal" | "compact";
    hideHeader?: boolean;
    /** When true (e.g. mobile drawer open), scroll feed to the latest note. */
    active?: boolean;
}

export default function NotesWidget({
    entityType,
    entityId,
    height = "100%",
    density = "normal",
    hideHeader = false,
    active = true,
}: NotesProps) {
    const [newComment, setNewComment] = useState("");
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState("");
    const [notesList, setNotesList] = useState<INote[]>([]);
    const [loading, setLoading] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);

    const { useList, useCreate, useUpdate, useDelete } = useNotes();
    const listSvc = useList();
    const createSvc = useCreate();
    const updateSvc = useUpdate();
    const deleteSvc = useDelete();

    const { user } = useAuth();

    const sortNotesOldestFirst = (notes: INote[]) =>
        [...notes].sort(
            (a, b) =>
                new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime(),
        );

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const result = await listSvc.mutateAsync({
                EntityType: entityType,
                EntityId: entityId,
            });
            setNotesList(sortNotesOldestFirst(Array.isArray(result) ? result : []));
        } catch (e) {
            console.error("Failed to fetch notes:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (entityId && entityType) {
            fetchNotes();
        }
    }, [entityId, entityType]);

    const scrollToBottom = () => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    };

    useEffect(() => {
        if (!active || loading) return;

        // Wait for layout after drawer open / list paint.
        const id = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(scrollToBottom);
        });
        return () => window.cancelAnimationFrame(id);
    }, [active, loading, notesList.length, density]);

    const handleSubmit = async () => {
        if (!newComment.trim()) return;
        try {
            await createSvc.mutateAsync({
                EntityType: entityType,
                EntityId: entityId,
                Content: newComment,
            });
            setNewComment("");
            fetchNotes();
        } catch (e) {
            console.error("Failed to add note:", e);
        }
    };

    const handleEditStart = (note: INote) => {
        setEditingNoteId(note.Id);
        setEditContent(note.Content);
    };

    const handleEditCancel = () => {
        setEditingNoteId(null);
        setEditContent("");
    };

    const handleEditSave = async (id: string) => {
        if (!editContent.trim()) return;
        try {
            await updateSvc.mutateAsync({ Id: id, Content: editContent });
            setEditingNoteId(null);
            fetchNotes();
        } catch (e) {
            console.error("Failed to update note:", e);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            try {
                await deleteSvc.mutateAsync(id);
                fetchNotes();
            } catch (e) {
                console.error("Failed to delete note:", e);
            }
        }
    };

    const getInitials = (name?: string) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    const isCompact = density === "compact";
    const rowPx = isCompact ? 1.5 : 2;

    return (
        <Box
            sx={{
                height: height,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.surface",
                borderRadius: 0,
                overflow: "hidden",
            }}
        >
            {!hideHeader && (
                <Box
                    sx={{
                        px: rowPx,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.level1",
                    }}
                >
                    <Box>
                        <Typography level="title-md">Project Notes</Typography>
                        <Skeleton loading={loading} variant="text" width={80}>
                            <Typography level="body-xs" color="neutral">
                                {notesList.length}{" "}
                                {notesList.length === 1 ? "entry" : "entries"}
                            </Typography>
                        </Skeleton>
                    </Box>
                </Box>
            )}

            {listSvc.isError && (
                <Box p={2}>
                    <Alert
                        errorMessage={
                            (listSvc.error as any)?.message ||
                            "Failed to load activity feed"
                        }
                    />
                </Box>
            )}

            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                        bgcolor: "neutral.outlinedBorder",
                        borderRadius: 0,
                    },
                }}
            >
                {!loading && notesList.length === 0 && (
                    <Box sx={{ px: rowPx, py: 4, textAlign: "center" }}>
                        <Typography level="body-sm" color="neutral">
                            No notes recorded.
                        </Typography>
                    </Box>
                )}

                {loading ? (
                    <Stack spacing={0}>
                        {[...Array(4)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    px: rowPx,
                                    py: 1.25,
                                    display: "flex",
                                    gap: 1.5,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Skeleton
                                    variant="circular"
                                    width={28}
                                    height={28}
                                />
                                <Stack spacing={0.5} sx={{ flex: 1 }}>
                                    <Skeleton
                                        variant="text"
                                        width="40%"
                                        height={12}
                                    />
                                    <Skeleton variant="text" width="90%" />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    notesList.map((note) => {
                        const isAuthor =
                            (user as any)?.user_id === note.CreatedBy ||
                            (user as any)?.Id === note.CreatedBy;
                        const isEditing = editingNoteId === note.Id;

                        return (
                            <Box
                                key={note.Id}
                                sx={{
                                    px: rowPx,
                                    py: 1.25,
                                    display: "flex",
                                    gap: 1.5,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    transition: "background-color 0.15s",
                                    "&:hover": { bgcolor: "background.level1" },
                                    "&:hover .note-actions": { opacity: 1 },
                                    "&:last-of-type": { borderBottom: "none" },
                                }}
                            >
                                <Avatar
                                    variant="soft"
                                    color={isAuthor ? "primary" : "neutral"}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        fontSize: "10px",
                                        flexShrink: 0,
                                    }}
                                >
                                    {getInitials(note.CreatedByName)}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 0,
                                        }}
                                    >
                                        <Typography level="body-xs">
                                            {note.CreatedByName || "User"}
                                        </Typography>
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                        >
                                            {moment(note.CreatedAt).fromNow()}
                                        </Typography>

                                        {isAuthor && !isEditing && (
                                            <Stack
                                                direction="row"
                                                className="note-actions"
                                                spacing={0.25}
                                                sx={{
                                                    ml: "auto",
                                                    opacity: 0,
                                                    transition: "opacity 0.15s",
                                                }}
                                            >
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() =>
                                                        handleEditStart(note)
                                                    }
                                                    sx={{
                                                        minWidth: 28,
                                                        minHeight: 28,
                                                    }}
                                                >
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="danger"
                                                    onClick={() =>
                                                        handleDelete(note.Id)
                                                    }
                                                    sx={{
                                                        minWidth: 28,
                                                        minHeight: 28,
                                                    }}
                                                >
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </Stack>
                                        )}
                                    </Box>

                                    {isEditing ? (
                                        <Box>
                                            <JoyTextarea
                                                size="sm"
                                                value={editContent}
                                                onChange={(e) =>
                                                    setEditContent(
                                                        e.target.value,
                                                    )
                                                }
                                                autoFocus
                                                sx={{
                                                    mb: 1,
                                                    borderRadius: "4px",
                                                    boxShadow: "none",
                                                    bgcolor:
                                                        "background.surface",
                                                }}
                                            />
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={handleEditCancel}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="soft"
                                                    color="primary"
                                                    onClick={() =>
                                                        handleEditSave(note.Id)
                                                    }
                                                    startDecorator={
                                                        <Check size={14} />
                                                    }
                                                >
                                                    Update
                                                </Button>
                                            </Stack>
                                        </Box>
                                    ) : (
                                        <Typography
                                            level="body-sm"
                                            sx={{
                                                color: "text.primary",
                                                lineHeight: 1.5,
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {note.Content}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>

            <Box
                sx={{
                    px: rowPx,
                    py: 1.25,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 1,
                        p: 1,
                        borderRadius: "4px",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.level1",
                    }}
                >
                    <JoyTextarea
                        placeholder="Write a note..."
                        variant="plain"
                        color="neutral"
                        size="sm"
                        minRows={1}
                        maxRows={5}
                        sx={{
                            flex: 1,
                            "--Textarea-focusedHighlight": "none",
                            "&::before": { display: "none" },
                            "&::after": { display: "none" },
                            bgcolor: "transparent",
                            fontSize: "sm",
                            boxShadow: "none",
                            border: "none",
                            "&.Mui-focused": {
                                boxShadow: "none",
                                outline: "none",
                            },
                        }}
                        slotProps={{
                            textarea: {
                                sx: {
                                    outline: "none",
                                    "&:focus": {
                                        outline: "none",
                                        boxShadow: "none",
                                    },
                                },
                            },
                        }}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                    />
                    <Button
                        size="sm"
                        variant="soft"
                        disabled={!newComment.trim()}
                        onClick={handleSubmit}
                        sx={{ flexShrink: 0, borderRadius: "4px" }}
                    >
                        Post
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
